import React from 'react'
import styled from 'styled-components'

import {
  Badge,
  CategoryFilterControl,
  Checkbox,
  KeyboardShortcut,
  SearchInput,
  Select,
  // @ts-expect-error: no types in this version of @gnomad/ui
} from '@gnomad/ui'

import CSVExportButton from '../CSVExportButton'
import { DatasetId } from '../Browser'
import { VariantCategoryOption } from '../variantCategories'
import { VariantRow, VariantTableColumn } from './variantTableColumns'

export const SettingsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1em;

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`

export const FiltersWrapper = styled.div`
  display: flex;
  flex-grow: 2;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: 1350px) {
    flex-direction: column;
  }

  @media (max-width: 700px) {
    align-items: center;
  }
`

const AnalysisGroupMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1em;

  @media (max-width: 700px) {
    flex-direction: column;
  }

  @media (min-width: 701px) {
    > * {
      margin-right: 0.5em;
    }
  }
`

export const FiltersFirstColumn = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 700px) {
    align-items: center;
  }
`

const FiltersSecondColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  label {
    margin-bottom: 0.375em;
  }

  @media (max-width: 1350px) {
    flex-direction: row;
    justify-content: flex-start;
    margin-top: 1em;

    label {
      margin-right: 2em;
    }
  }

  @media (max-width: 700px) {
    flex-direction: column;
  }
`

const ExportNote = styled.div`
  margin-top: 0.5em;
  max-width: 30em;
`

const VariantColumnGroupCheckboxes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em 1em;

  label {
    white-space: nowrap;
  }
`

const SearchWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;

  @media (max-width: 1100px) {
    margin-top: 0.525em;
  }
`

export interface FilterState {
  includeCategories: Record<string, boolean>
  searchText: string
  custom: any // TK: TODO: fixme: any!!,
  gp2VariantColumnGroups?: Record<string, boolean>
  agbVariantColumnGroups?: Record<string, boolean>
}

interface VariantFilterControlProps {
  datasetId: DatasetId
  variantCategoryOptions: VariantCategoryOption[]
  customFilterComponent: any
  exportNote?: string
  filter: FilterState
  geneId: string
  onChangeAnalysisGroup: (analysisGroup: string) => void
  onChangeFilter: (filter: FilterState) => void
  renderedVariants: VariantRow[]
  selectedAnalysisGroup: string
  variantAnalysisGroupLabels: Record<string, string>
  variantAnalysisGroupOptions: readonly string[]
  variantTableColumns: VariantTableColumn[]
}

const VariantFilterControls = ({
  datasetId,
  variantCategoryOptions,
  customFilterComponent: CustomFilterComponent,
  exportNote,
  filter,
  geneId,
  onChangeAnalysisGroup,
  onChangeFilter,
  renderedVariants,
  selectedAnalysisGroup,
  variantAnalysisGroupLabels,
  variantAnalysisGroupOptions,
  variantTableColumns,
}: VariantFilterControlProps) => {
  const gp2Checkboxes = [
    { key: 'pd', label: 'PD' },
    { key: 'psp', label: 'PSP' },
    { key: 'dlb', label: 'DLB' },
    { key: 'msa', label: 'MSA' },
  ]

  const agbCheckboxes = [
    { key: 'deNovo', label: 'De novo' },
    { key: 'transmittedUntransmitted', label: 'Transmitted / Untransmitted' },
    { key: 'caseControl', label: 'Case / Control' },
  ]

  // `heading` may be a column-group banner element rather than plain text; `csvHeading`
  // carries the underlying text for CSV export.
  const csvExportColumns = variantTableColumns.map((column) => ({
    ...column,
    heading:
      column.csvHeading || (typeof column.heading === 'string' ? column.heading : column.key),
  }))

  return (
    <SettingsWrapper>
      <FiltersWrapper>
        <FiltersFirstColumn>
          <CategoryFilterControl
            categories={variantCategoryOptions.map((option) => ({
              id: option.id,
              label: option.label,
              className: 'category',
              color: option.color,
            }))}
            categorySelections={filter.includeCategories}
            id="variant-consequence-category-filter"
            onChange={(includeCategories: Record<string, boolean>) => {
              onChangeFilter({ ...filter, includeCategories })
            }}
          />
          {variantCategoryOptions
            .filter((option) => option.keyboardShortcut)
            .map((option) => (
              <KeyboardShortcut
                key={option.id}
                handler={() => {
                  onChangeFilter({
                    ...filter,
                    includeCategories: {
                      ...filter.includeCategories,
                      [option.id]: !filter.includeCategories[option.id],
                    },
                  })
                }}
                keys={option.keyboardShortcut}
              />
            ))}

          <AnalysisGroupMenuWrapper>
            {variantAnalysisGroupOptions.length > 1 && (
              <>
                {/* eslint-disable-next-line jsx-a11y/label-has-for,jsx-a11y/label-has-associated-control */}
                <label htmlFor="analysis-group">Current analysis group</label>
                <Select
                  id="analysis-group"
                  value={selectedAnalysisGroup}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onChangeAnalysisGroup(e.target.value)
                  }
                  style={{ paddingRight: '10em' }}
                >
                  {variantAnalysisGroupOptions.map((group) => (
                    <option key={group} value={group}>
                      {variantAnalysisGroupLabels[group] || group}
                    </option>
                  ))}
                </Select>
              </>
            )}

            <CSVExportButton
              data={renderedVariants}
              columns={csvExportColumns}
              filename={`${selectedAnalysisGroup}_${geneId}_variants`}
            >
              Export variants to CSV
            </CSVExportButton>
          </AnalysisGroupMenuWrapper>

          {exportNote && (
            <ExportNote>
              <Badge level="info">Note</Badge> {exportNote}
            </ExportNote>
          )}
        </FiltersFirstColumn>

        <FiltersSecondColumn>
          {CustomFilterComponent && (
            <CustomFilterComponent
              value={filter.custom}
              onChange={(customFilter: any) => {
                onChangeFilter({ ...filter, custom: customFilter })
              }}
            />
          )}

          {datasetId === 'GP2' && filter.gp2VariantColumnGroups && (
            <div style={{ marginTop: CustomFilterComponent ? '1em' : '0' }}>
              <span style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5em' }}>
                GP2 Case groups included in table columns
              </span>
              <VariantColumnGroupCheckboxes>
                {gp2Checkboxes.map(({ key, label }) => (
                  <Checkbox
                    key={key}
                    id={`gp2-group-checkbox-${key}`}
                    label={label}
                    checked={filter.gp2VariantColumnGroups![key]}
                    onChange={(isChecked: boolean) => {
                      onChangeFilter({
                        ...filter,
                        gp2VariantColumnGroups: {
                          ...filter.gp2VariantColumnGroups,
                          [key]: isChecked,
                        },
                      })
                    }}
                  />
                ))}
              </VariantColumnGroupCheckboxes>
            </div>
          )}

          {datasetId === 'AGB' && filter.agbVariantColumnGroups && (
            <div style={{ marginTop: CustomFilterComponent ? '1em' : '0' }}>
              <span style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5em' }}>
                AC columns included in table
              </span>
              <VariantColumnGroupCheckboxes>
                {agbCheckboxes.map(({ key, label }) => (
                  <Checkbox
                    key={key}
                    id={`agb-group-checkbox-${key}`}
                    label={label}
                    checked={filter.agbVariantColumnGroups![key]}
                    onChange={(isChecked: boolean) => {
                      onChangeFilter({
                        ...filter,
                        agbVariantColumnGroups: {
                          ...filter.agbVariantColumnGroups,
                          [key]: isChecked,
                        },
                      })
                    }}
                  />
                ))}
              </VariantColumnGroupCheckboxes>
            </div>
          )}
        </FiltersSecondColumn>
      </FiltersWrapper>

      <SearchWrapper>
        <SearchInput
          placeholder="Search variant table"
          value={filter.searchText}
          onChange={(searchText: string) => {
            onChangeFilter({ ...filter, searchText })
          }}
        />
      </SearchWrapper>
    </SettingsWrapper>
  )
}

export default VariantFilterControls
